import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  CogIcon,
  XMarkIcon,
  KeyIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { isDM510 } from '../../utils/modelLabel';
import { SettingsReadOnlyContext } from './readOnlyContext';
import MoistureSetup from './MoistureSetup';
import DischargeRateSetup from './DischargeRateSetup';
import DryingTemperatureSetup from './DryingTemperatureSetup';
import VolumeSetup from './VolumeSetup';
import AlarmSettings from './AlarmSettings';
import AdvancedSettings from './AdvancedSettings';
import Panel510Setup from './Panel510Setup';

const SettingsMain = ({ deviceSerial, onClose, readOnly = false, model = '' }) => {
  const { getTopicData, subscribe, unsubscribe, isSubscribed, connectionState, sendUpdate } = useWebSocket();
  // A DM510 publishes its dryer figures and alarm limits under the same names as
  // the 2500i, so it gets those two pages as they are, plus a short Misc page of
  // its own (product, dryer-off rate, unit, panel software); it has none of the
  // rate, temperature, sensor or model pages, so those stay off its menu.
  const is510 = isDM510(model);
  // A DM510's panel takes a written word as unsigned (an inlet temperature limit of
  // -31 arrived as 65505), so until its write side is understood the DM510's pages
  // show the panel's values for reference only. The 2500i keeps its editors.
  const panelLocked = is510;
  const [settingsData, setSettingsData] = useState(null);
  const [activeCategory, setActiveCategory] = useState('volume'); // Default to volume
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [pendingUpdates, setPendingUpdates] = useState({}); // { fieldName: { value, timestamp } }

  // Settings categories menu. Titles follow the panel's own menu, so an
  // employee on the phone to a customer is reading the same words they are.
  const categories = is510 ? [
    {
      id: 'volume',
      title: 'Dryer Volumes & Throughput',
      component: VolumeSetup
    },
    {
      id: 'alarms',
      title: 'Alarms',
      component: AlarmSettings
    },
    {
      id: 'misc',
      title: 'Misc',
      component: Panel510Setup
    }
  ] : [
    {
      id: 'volume',
      title: 'Dryer Volumes & Throughput',
      component: VolumeSetup
    },
    {
      id: 'discharge',
      title: 'Discharge Rate',
      component: DischargeRateSetup
    },
    {
      id: 'temperature',
      title: 'Drying Temperature',
      component: DryingTemperatureSetup
    },
    {
      id: 'moisture',
      title: 'Moisture Sensors',
      component: MoistureSetup
    },
    {
      id: 'alarms',
      title: 'Alarms',
      component: AlarmSettings
    },
    {
      id: 'advanced',
      title: 'Advanced Settings',
      component: AdvancedSettings
    }
  ];
  
  // isSubscribed is rebuilt every time anything subscribes, so naming it as a
  // dependency would tear this effect down and set it up again on its own
  // result. The socket helpers are read through a ref instead, and the effect
  // runs once per device.
  const ws = useRef(null);
  ws.current = { subscribe, unsubscribe, isSubscribed };

  // Subscriptions are not counted per viewer, so on the way out we release the
  // stream only if we were the ones who started it.
  const claimedSettings = useRef(false);
  useEffect(() => {
    if (!deviceSerial) return;
    claimedSettings.current = !ws.current.isSubscribed(deviceSerial, 'settings');
    if (claimedSettings.current) ws.current.subscribe(deviceSerial, 'settings');
    return () => {
      if (claimedSettings.current) ws.current.unsubscribe(deviceSerial, 'settings');
      claimedSettings.current = false;
    };
  }, [deviceSerial]);
  
  // Get settings data - poll for updates, but preserve pending (in-flight) values
  useEffect(() => {
    const interval = setInterval(() => {
      const topicData = getTopicData(deviceSerial, 'settings');
      if (topicData?.data) {
        let incoming = topicData.data.settings;

        // If we have pending updates, keep our optimistic values until stream catches up
        if (incoming && Object.keys(pendingUpdates).length > 0) {
          const now = Date.now();
          incoming = incoming.map(item => {
            const pending = pendingUpdates[item.name];
            if (!pending) return item;
            // If stream now shows the new value, or 10s timeout — clear pending
            if (item.value === pending.value || now - pending.timestamp > 10000) {
              setPendingUpdates(prev => {
                const next = { ...prev };
                delete next[item.name];
                return next;
              });
              return item;
            }
            // Stream still has old value — keep our optimistic value
            return { ...item, value: pending.value };
          });
        }

        setSettingsData(incoming);
        setDeviceInfo(topicData.data.deviceInfo);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [getTopicData, deviceSerial, pendingUpdates]);
  
  // Handle value edit. Booleans and non-numeric strings (enum values like
  // density_unit) pass through untouched; numeric edits arrive as input
  // strings and get parsed.
  const handleEdit = async (name, value) => {
    setLoading(true);
    setError(null);
    const parsed = parseFloat(value);
    const numValue = typeof value === 'boolean' || Number.isNaN(parsed) ? value : parsed;

    // Mark as pending so the stream doesn't overwrite with old data
    setPendingUpdates(prev => ({ ...prev, [name]: { value: numValue, timestamp: Date.now() } }));

    // Update local state optimistically
    setSettingsData(prev =>
      prev?.map(item =>
        item.name === name ? { ...item, value: numValue } : item
      )
    );

    try {
      await sendUpdate(deviceSerial, 'settings', [{
        path: name,
        value: numValue
      }]);
      console.log(`[Settings] Successfully updated ${name} to ${value}`);
    } catch (err) {
      console.error(`[Settings] Failed to update ${name}:`, err);
      setError(`Failed to update ${name}: ${err.message}`);
      // Clear pending on error so stream can correct the value
      setPendingUpdates(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle mode change (sends string value like 'local_mode', 'manual_mode')
  const handleModeChange = async (modeValue) => {
    setLoading(true);
    setError(null);

    // Map mode string to numeric for optimistic UI update
    const modeNumMap = { local_mode: 0, manual_mode: 1, automatic_mode: 2 };
    const numValue = modeNumMap[modeValue] ?? modeValue;
    setPendingUpdates(prev => ({ ...prev, dryer_master_mode: { value: numValue, timestamp: Date.now() } }));
    setSettingsData(prev =>
      prev?.map(item =>
        item.name === 'dryer_master_mode' ? { ...item, value: numValue } : item
      )
    );

    try {
      await sendUpdate(deviceSerial, 'settings', [{ path: 'mode', value: modeValue }]);
      console.log(`[Settings] Mode changed to ${modeValue}`);
    } catch (err) {
      console.error(`[Settings] Failed to change mode:`, err);
      setError(`Failed to change mode: ${err.message}`);
      setPendingUpdates(prev => {
        const next = { ...prev };
        delete next.dryer_master_mode;
        return next;
      });
    } finally {
      setLoading(false);
    }
  };

  // Get setting value by name
  const getSettingValue = (name) => {
    const setting = settingsData?.find(s => s.name === name);
    return setting || { value: null, minRange: null, maxRange: null };
  };
  
  // Get current category
  const currentCategory = categories.find(cat => cat.id === activeCategory);
  const CurrentComponent = currentCategory?.component;
  
  return (
    <SettingsReadOnlyContext.Provider value={readOnly || panelLocked}>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-gray-900/95 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-100 rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header with Device Info and Password */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <CogIcon className="w-8 h-8 text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white">Settings</h2>
              <p className="text-blue-100 text-sm">Serial: {deviceSerial}</p>
            </div>
          </div>
          
          {/* Admin Helper Info — the register password never reaches vendor
              sessions (the cloud strips it from their stream), so the chip
              only renders for staff */}
          <div className="flex items-center gap-6">
            {!readOnly && deviceInfo && (
              <div className="bg-white/20 backdrop-blur rounded-lg px-4 py-2 flex items-center gap-3">
                <KeyIcon className="w-5 h-5 text-yellow-300" />
                <div className="text-white">
                  <p className="text-xs opacity-90">Device Password (for admin reference)</p>
                  <p className="font-mono font-bold">{deviceInfo.registerPassword || 'N/A'}</p>
                </div>
              </div>
            )}
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>
        
        {/* Connection Status */}
        {connectionState !== 'connected' && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-2 flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800">Live link lost — reconnecting. Values shown may be stale.</span>
          </div>
        )}
        
        {/* The DM510 lock: values shown, nothing written from here */}
        {panelLocked && (
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-2 flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-blue-600" />
            <span className="text-blue-800">Shown for reference: changes from here are switched off for the DM510 for now. Change a value on the panel itself.</span>
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 px-6 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
              <span className="text-red-800">{error}</span>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        )}
        
        {/* Main Content with Sidebar */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar Navigation */}
          <div className="w-80 bg-gray-50 border-r border-gray-300 overflow-y-auto">
            <div className="space-y-0">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`w-full text-left px-6 py-5 transition-all border-b border-gray-200 ${
                    activeCategory === category.id
                      ? 'bg-[#2a4e7c] text-white shadow-lg'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span className="font-medium text-lg">{category.title}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Right Content Area */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            {settingsData ? (
              <div className="max-w-5xl mx-auto">
                {CurrentComponent && (
                  <CurrentComponent
                    settingsData={settingsData}
                    getSettingValue={getSettingValue}
                    handleEdit={handleEdit}
                    handleModeChange={handleModeChange}
                    onBack={() => setActiveCategory('volume')}
                  />
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <ArrowPathIcon className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-spin" />
                  <p className="text-gray-600">Loading settings...</p>
                  <p className="text-sm text-gray-400 mt-2">
                    If this doesn't load within a few seconds, the dryer's cloud link may be down.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Footer with Loading State */}
        {loading && (
          <div className="bg-blue-50 border-t border-blue-200 px-6 py-3 flex items-center gap-2">
            <ArrowPathIcon className="w-5 h-5 text-blue-600 animate-spin" />
            <span className="text-blue-800">Updating setting...</span>
          </div>
        )}
      </motion.div>
    </motion.div>
    </SettingsReadOnlyContext.Provider>
  );
};

export default SettingsMain;